import React from "react";
import "../CSS/ContentPage.css";

const PrivacyPolicy = () => {
  return (
    <>
      <title>Privacy Policy | Data Protection & Security - Syncnode IT Services</title>
      <meta name="description" content="Syncnode's privacy policy for IT services. Learn how we collect, use, and protect your personal information. SSL encryption, secure payment processing, GDPR compliant." />
      <meta name="keywords" content="privacy policy, data protection, GDPR compliance, data security, personal information, IT services privacy, Syncnode privacy" />
      <link rel="canonical" href="https://www.syncnode.in/privacypolicy" />
      <meta property="og:title" content="Privacy Policy | Data Protection & Security - Syncnode" />
      <meta property="og:description" content="Learn how Syncnode protects your privacy and personal information with industry-standard security measures for IT services." />
      <meta property="og:url" content="https://www.syncnode.in/privacypolicy" />
      <meta property="og:type" content="website" />
      <div className="contentpage_wrapper">
        <div className="container">
          <div className="content_banner">
            <h1>
              Privacy <span className="decorated_text">Policy</span>
            </h1>
            <p className="intro_text">
              Your privacy is important to us. Learn how we collect, use, and
              protect your personal information.
            </p>

            <div className="last_updated">
              <p>Last Updated: October 31, 2025</p>
            </div>
          </div>
          <div className="main_content">
            <h2 className="section_heading">Introduction</h2>
            <p className="content_para">
              Welcome to Syncnode ("we," "our," or "us"). We are committed to
              protecting your personal information and your right to privacy.
              This Privacy Policy explains how we collect, use, disclose, and
              safeguard your information when you visit our website or use our
              services.
            </p>
            <p className="content_para">
              By using our services, you agree to the collection and use of
              information in accordance with this policy. If you do not agree
              with our policies and practices, please do not use our services.
            </p>

            <h5 className="section_sub_heading">
              Personal Information You Provide
            </h5>
            <p className="content_para">
              We collect the following personal information when you interact
              with our services:
            </p>
            <ul>
              <li>
                <strong>Contact Information:</strong> Name, email address, phone
                number, and mailing address
              </li>
              <li>
                <strong>Account Information:</strong> Username, password, and
                account preferences
              </li>
              <li>
                <strong>Business Information</strong>: Company name, job title,
                and business details
              </li>
              <li>
                <strong>Project Information</strong>: Project requirements,
                specifications, and related communications
              </li>
              <li>
                <strong>Payment Information</strong>: Billing address, payment
                method details (processed securely through third-party payment
                processors)
              </li>
            </ul>
            <h5 className="section_sub_heading">
              Information Collected Automatically
            </h5>
            <p className="content_para">
              When you access our website or services, we automatically collect
              certain information:
            </p>
            <ul>
              <li>
                <strong>Device Information:</strong> IP address, browser type,
                operating system, and device identifiers
              </li>
              <li>
                <strong>Usage Information:</strong> Pages visited, time spent on
                pages, links clicked, and navigation paths
              </li>
              <li>
                <strong>Location Data:</strong> General geographic location
                based on IP address
              </li>
              <li>
                <strong>Cookies and Tracking Technologies:</strong> Information
                collected through cookies, web beacons, and similar technologies
              </li>
            </ul>

            <h2 className="section_heading">How We Use Your Information</h2>
            <p className="content_para">
              We use the information we collect for various purposes, including:
            </p>
            <ul>
              <li>
                <strong>Service Delivery:</strong> To provide, maintain, and
                improve our services
              </li>
              <li>
                <strong>Communication:</strong> To respond to inquiries, send
                updates, and provide customer support
              </li>
              <li>
                <strong>Project Management:</strong> To manage and deliver your
                projects effectively
              </li>
              <li>
                <strong>Payment Processing:</strong> To process transactions and
                send billing information
              </li>
              <li>
                <strong>Marketing:</strong> To send promotional materials and
                updates about our services (with your consent)
              </li>
              <li>
                <strong>Analytics:</strong> To understand how our services are
                used and improve user experience
              </li>
              <li>
                <strong>Legal Compliance:</strong> To comply with legal
                obligations and protect our rights
              </li>
              <li>
                <strong>Security:</strong> To detect, prevent, and address
                technical issues and fraudulent activity
              </li>
            </ul>

            <h2 className="section_heading">
              Payment Information and Processing
            </h2>
            <p className="content_para">
              When you make a payment for our services, we collect billing
              information including your name, billing address, and payment
              method details. We use a secure third-party payment processor to
              handle all payment transactions.
            </p>
            <p className="content_para">
              <strong>Important:</strong> We do not store your complete credit
              card information on our servers. All payment data is encrypted and
              processed through PCI-DSS compliant payment gateway Cashfree.
            </p>

            <h5 className="section_sub_heading">Payment Processor</h5>
            <p className="content_para">
              We work with Cashfree as our trusted payment processor for UPI,
              net banking, credit/debit cards, and other payment methods in
              India. Cashfree has its own privacy policy governing the use of
              your information. We encourage you to review their policy.
            </p>

            <h2 className="section_heading">
              Information Sharing and Disclosure
            </h2>
            <p className="content_para">
              We do not sell, trade, or rent your personal information to third
              parties. We may share your information in the following
              circumstances:
            </p>
            <ul>
              <li>
                <strong>Service Providers:</strong> With third-party vendors who
                perform services on our behalf (hosting, analytics, payment
                processing)
              </li>
              <li>
                <strong>Business Transfers:</strong> In connection with mergers,
                acquisitions, or sale of assets
              </li>
              <li>
                <strong>Legal Requirements:</strong> When required by law or to
                protect our rights and safety
              </li>
              <li>
                <strong>With Your Consent:</strong> When you explicitly
                authorize us to share your information
              </li>
            </ul>

            <h2 className="section_heading">Data Security</h2>
            <p className="content_para">
              We implement appropriate technical and organizational security
              measures to protect your personal information against unauthorized
              access, alteration, disclosure, or destruction. These measures
              include:
            </p>
            <ul>
              <li>SSL/TLS encryption for data transmission</li>
              <li>
                Secure server infrastructure with regular security updates
              </li>
              <li>Access controls and authentication mechanisms</li>
              <li>Regular security audits and vulnerability assessments</li>
              <li>Employee training on data protection practices</li>
            </ul>
            <p className="content_para">
              However, no method of transmission over the internet or electronic
              storage is completely secure. While we strive to protect your
              information, we cannot guarantee absolute security.
            </p>

            <h2 className="section_heading">Data Retention</h2>
            <p className="content_para">
              We retain your personal information for as long as necessary to
              fulfill the purposes outlined in this Privacy Policy, unless a
              longer retention period is required or permitted by law. When your
              information is no longer needed, we will securely delete or
              anonymize it.
            </p>

            <h2 className="section_heading">Your Rights and Choices</h2>
            <p className="content_para">
              Depending on your location, you may have certain rights regarding
              your personal information:
            </p>
            <ul>
              <li>
                <strong>Access:</strong> Request access to the personal
                information we hold about you
              </li>
              <li>
                <strong>Correction:</strong> Request correction of inaccurate or
                incomplete information
              </li>
              <li>
                <strong>Deletion:</strong> Request deletion of your personal
                information
              </li>
              <li>
                <strong>Portability:</strong> Request a copy of your information
                in a structured, machine-readable format
              </li>
              <li>
                <strong>Objection:</strong> Object to the processing of your
                information in certain circumstances
              </li>
              <li>
                <strong>Opt-Out:</strong> Unsubscribe from marketing
                communications at any time
              </li>
            </ul>
            <p className="content_para">
              To exercise these rights, please contact us at{" "}
              <strong>privacy@syncnode.in</strong>.
            </p>

            <h2 className="section_heading">
              Cookies and Tracking Technologies
            </h2>
            <p className="content_para">
              We use cookies and similar tracking technologies to enhance your
              experience on our website. Cookies are small data files stored on
              your device that help us understand how you use our services.
            </p>

            <h5 className="section_sub_heading">Types of Cookies We Use</h5>
            <ul>
              <li>
                <strong>Essential Cookies:</strong> Necessary for the website to
                function properly
              </li>
              <li>
                <strong>Analytics Cookies:</strong> Help us understand how
                visitors interact with our website
              </li>
              <li>
                <strong>Functionality Cookies:</strong> Remember your
                preferences and settings
              </li>
              <li>
                <strong>Marketing Cookies:</strong> Track your activity to
                deliver relevant advertisements
              </li>
            </ul>
            <p className="content_para">
              You can control cookies through your browser settings. However,
              disabling cookies may affect your ability to use certain features
              of our website.
            </p>

            <h2 className="section_heading">Third-Party Links</h2>
            <p className="content_para">
              Our website may contain links to third-party websites. We are not
              responsible for the privacy practices or content of these external
              sites. We encourage you to review the privacy policies of any
              third-party sites you visit.
            </p>

            <h2 className="section_heading">Children's Privacy</h2>
            <p className="content_para">
              Our services are not directed to individuals under the age of 18.
              We do not knowingly collect personal information from children. If
              we become aware that we have collected information from a child
              without parental consent, we will take steps to delete that
              information.
            </p>

            <h2 className="section_heading">International Data Transfers</h2>
            <p className="content_para">
              Your information may be transferred to and processed in countries
              other than your country of residence. These countries may have
              different data protection laws. We ensure appropriate safeguards
              are in place to protect your information in accordance with this
              Privacy Policy.
            </p>

            <h2 className="section_heading">Changes to This Privacy Policy</h2>
            <p className="content_para">
              We may update this Privacy Policy from time to time to reflect
              changes in our practices or legal requirements. We will notify you
              of any material changes by posting the updated policy on our
              website and updating the "Last Updated" date. Your continued use
              of our services after such changes constitutes acceptance of the
              updated policy.
            </p>

            <h2 className="section_heading">Contact Us</h2>
            <p className="content_para">
              If you have any questions, concerns, or requests regarding this
              Privacy Policy or our data practices, please contact us:
            </p>
            <div className="highlight_box">
              <ul>
                <li>
                  <strong>Email:</strong> info@syncnode.in
                </li>
                <li>
                  <strong>Phone:</strong> +91 7876666787
                </li>
                <li>
                  <strong>Address:</strong> Hisar, Haryana, India
                </li>
                <li>
                  <strong> Business Hours:</strong> 9:00 AM - 6:00 PM IST,
                  Monday to Friday
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PrivacyPolicy;
